// =====================================================================
// SecuriteTournee.jsx — la tournée de Vincent : qui fait quelle vérification (23-10-2026)
// =====================================================================
// Demande de Silviu : « să poată să selecteze una sau mai multe pentru fiecare tache.
// Dacă pentru mesele de packing atribuie 2 persoane, când vrea să atribuie pentru marfă
// sau paleții de pe alee, persoanele deja atribuite … să nu mai apară. »
// Donc : chaque vérification reçoit une ou plusieurs personnes ; une personne déjà
// placée n'est plus proposée ailleurs ; une personne en congé ce jour-là n'est jamais
// proposée. La tournée arrive pré-remplie avec la répartition de la précédente :
// Vincent ne corrige que l'écart. Le serveur refuse aussi toute combinaison impossible.
// =====================================================================

import { useState, useEffect, useCallback } from 'react';
import { safetyRoutines, errMsg } from '../services/api.js';
import { useAuth } from '../hooks/useAuth.jsx';
import { libelleFrequence, personnesLibres } from '../lib/tournee.js';
import SecuriteVerificationLigne from './SecuriteVerificationLigne.jsx';

const C = {
  line: '#e5e7eb', ink: '#3f4654', mute: '#7b8494', brand: '#714B67', ok: '#16a34a', okBg: '#effaf3',
  crit: '#dc2626', critBg: '#fdf0f0', warn: '#d97706', warnBg: '#fdf5e7', bg: '#f8fafc',
};
const card = { background: '#fff', border: `1px solid ${C.line}`, borderRadius: 10, padding: '1rem 1.1rem', marginBottom: '1rem' };
const btn = (bg, fg = '#fff') => ({
  background: bg, color: fg, border: bg === '#fff' ? `1px solid ${C.line}` : '1px solid transparent',
  borderRadius: 8, padding: '0.6rem 1rem', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
});
const STATUT = {
  A_PLANIFIER: 'à répartir', ASSIGNE: 'répartie', EN_COURS: 'en cours', EN_RETARD: 'en retard',
  FAIT: 'faite', NON_CONFORME: 'faite — non-conformités', INCOMPLET: 'close incomplète', ANNULE: 'annulée',
};
const jour = (d) => new Date(d).toLocaleDateString('fr-BE', { weekday: 'long', day: 'numeric', month: 'long' });

export default function SecuriteTournee() {
  const { user } = useAuth();
  const [tournees, setTournees] = useState(null);
  const [selId, setSelId] = useState(null);
  const [att, setAtt] = useState(null);                 // réponse du serveur
  const [repartition, setRepartition] = useState({});   // { itemId: [userId] } en cours d'édition
  const [moiMeme, setMoiMeme] = useState(null);         // ligne reprise par Vincent
  const [week, setWeek] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [flash, setFlash] = useState(null);

  const charger = useCallback(async () => {
    try {
      const [t, w] = await Promise.all([safetyRoutines.tournees(), safetyRoutines.week()]);
      setTournees(t); setWeek(w);
      setSelId((id) => id || t.ouvertes[0]?.id || null);
    } catch (e) { setErr(errMsg(e)); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const chargerAttribution = useCallback(async (id) => {
    if (!id) { setAtt(null); return; }
    try {
      const a = await safetyRoutines.attribution(id);
      setAtt(a);
      setRepartition(Object.fromEntries(a.lignes.map((l) => [l.itemId, l.personnes.map((p) => p.id)])));
    } catch (e) { setErr(errMsg(e)); }
  }, []);
  useEffect(() => { chargerAttribution(selId); }, [selId, chargerAttribution]);

  const noms = Object.fromEntries((att?.personnes || []).map((p) => [p.id, p.name]));
  for (const l of att?.lignes || []) for (const p of l.personnes) noms[p.id] = noms[p.id] || p.name;
  const close = Boolean(att?.occurrence?.lockedAt);
  const modifiee = att && att.lignes.some((l) => JSON.stringify([...(repartition[l.itemId] || [])].sort())
    !== JSON.stringify(l.personnes.map((p) => p.id).sort()));

  const ajouter = (itemId, userId) => userId && setRepartition((r) => ({ ...r, [itemId]: [...(r[itemId] || []), userId] }));
  const retirer = (itemId, userId) => setRepartition((r) => ({ ...r, [itemId]: (r[itemId] || []).filter((u) => u !== userId) }));

  async function enregistrer() {
    setBusy(true); setErr(null);
    try {
      const a = await safetyRoutines.saveAttribution(att.occurrence.id, {
        lignes: att.lignes.map((l) => ({ itemId: l.itemId, userIds: repartition[l.itemId] || [] })),
      });
      setAtt(a);
      setRepartition(Object.fromEntries(a.lignes.map((l) => [l.itemId, l.personnes.map((p) => p.id)])));
      setFlash('Répartition enregistrée : chacun voit sa vérification dans « Mes vérifications ».');
      charger();
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  }

  if (moiMeme) {
    return (
      <SecuriteVerificationLigne ligne={moiMeme} moi={user?.id} reprise
        onRetour={() => { setMoiMeme(null); chargerAttribution(selId); }}
        onFini={() => { setMoiMeme(null); setFlash('Vérification enregistrée.'); chargerAttribution(selId); charger(); }} />
    );
  }

  const libres = att ? personnesLibres(att.personnes, repartition) : [];
  const enConge = (att?.personnes || []).filter((p) => p.conge);

  return (
    <div>
      {err && <div style={{ ...card, background: C.critBg, borderColor: '#f6cfcf', color: '#991B1B' }}>{err}</div>}
      {flash && <div style={{ ...card, background: C.okBg, borderColor: '#cfead9', color: '#065F46', fontWeight: 600 }}>{flash}</div>}

      {/* ---------- les tournées ---------- */}
      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', marginBottom: '1rem' }}>
        {[...(tournees?.ouvertes || []), ...(tournees?.closes || [])].map((t) => (
          <button key={t.id} type="button" onClick={() => { setSelId(t.id); setFlash(null); setErr(null); }} style={{
            textAlign: 'left', background: '#fff', borderRadius: 10, padding: '0.8rem 0.9rem', cursor: 'pointer',
            border: `2px solid ${selId === t.id ? C.brand : (t.enRetard ? '#f6cfcf' : C.line)}`, opacity: t.close ? 0.75 : 1,
          }}>
            <div style={{ fontWeight: 700, color: C.ink }}>{t.templateName}</div>
            <div style={{ fontSize: '0.85rem', color: C.mute }}>{jour(t.dueDate)}</div>
            <div style={{ height: 6, background: '#eef2f7', borderRadius: 99, margin: '8px 0 6px', overflow: 'hidden' }}>
              <div style={{ width: `${t.total ? Math.round((t.faites / t.total) * 100) : 0}%`, height: '100%', background: t.nonConformes ? C.warn : C.ok }} />
            </div>
            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: t.sansPersonne && !t.close ? C.crit : C.mute }}>
              {t.faites}/{t.total} faite(s) · {STATUT[t.status] || t.status}
              {!t.close && t.sansPersonne > 0 && ` · ${t.sansPersonne} sans personne`}
            </div>
          </button>
        ))}
        {tournees && !tournees.ouvertes.length && !tournees.closes.length && (
          <div style={{ color: C.mute }}>Aucune tournée pour le moment : elle se crée toute seule avant la date due.</div>
        )}
      </div>

      {/* ---------- la répartition ---------- */}
      {att && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>{att.occurrence.templateName} — {jour(att.occurrence.dueDate)}</div>
              <div style={{ fontSize: '0.85rem', color: C.mute }}>
                {close ? 'Tournée close : l’historique ne se réécrit pas.'
                  : 'Une ou plusieurs personnes par vérification. Une personne déjà placée n’est plus proposée ailleurs ; une personne en congé n’est pas proposée.'}
              </div>
            </div>
            {!close && (
              <button type="button" disabled={busy || !modifiee} onClick={enregistrer}
                style={{ ...btn(modifiee ? C.brand : '#cbd5e1'), cursor: modifiee ? 'pointer' : 'default' }}>
                {busy ? 'Enregistrement…' : 'Enregistrer la répartition'}
              </button>
            )}
          </div>

          {!close && (
            <div style={{ fontSize: '0.86rem', color: C.ink, margin: '10px 0 4px' }}>
              <b>Libres :</b> {libres.length ? libres.map((p) => p.name).join(', ') : 'personne'}
              {enConge.length > 0 && <span style={{ color: C.mute }}> · <b>en congé :</b> {enConge.map((p) => p.name).join(', ')}</span>}
            </div>
          )}

          {att.lignes.map((l) => {
            const ici = repartition[l.itemId] || [];
            const vide = !l.faite && !ici.length;
            return (
              <div key={l.itemId} style={{
                borderTop: `1px solid ${C.line}`, padding: '10px 0',
                ...(vide && !close ? { background: C.critBg, margin: '0 -1.1rem', padding: '10px 1.1rem' } : {}),
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ fontWeight: 600, color: C.ink }}>
                    {l.label}
                    <span style={{ fontWeight: 500, fontSize: '0.8rem', color: C.mute }}> · {libelleFrequence(l.periodicityWeeks)}</span>
                  </div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: l.faite ? (l.reponse === 'OK' || l.reponse === 'NA' ? C.ok : C.crit) : (vide ? C.crit : C.mute) }}>
                    {l.faite ? `✓ ${l.reponse}${l.faitePar ? ` — ${l.faitePar}` : ''}` : (vide ? 'Personne' : 'À faire')}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
                  {ici.map((u) => (
                    <span key={u} style={{ background: '#eef2ff', color: '#3730a3', borderRadius: 99, padding: '4px 6px 4px 12px', fontSize: '0.88rem', fontWeight: 600 }}>
                      {noms[u] || u}
                      {!close && !l.faite && (
                        <button type="button" onClick={() => retirer(l.itemId, u)} title="Retirer"
                          style={{ border: 'none', background: 'none', color: '#3730a3', fontWeight: 800, cursor: 'pointer', marginLeft: 4, fontSize: '1rem' }}>×</button>
                      )}
                    </span>
                  ))}
                  {!close && !l.faite && (
                    <select value="" onChange={(e) => ajouter(l.itemId, e.target.value)}
                      disabled={!libres.length}
                      style={{ padding: '6px 8px', border: `1px solid ${C.line}`, borderRadius: 8, fontSize: '0.9rem', minWidth: 180 }}>
                      <option value="">{libres.length ? '+ ajouter une personne' : 'plus personne de libre'}</option>
                      {libres.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  )}
                  {!close && !l.faite && !modifiee && (
                    <button type="button" onClick={() => setMoiMeme({
                      occurrenceId: att.occurrence.id, itemId: l.itemId, label: l.label, labelRo: l.labelRo,
                      dueDate: att.occurrence.dueDate, templateName: att.occurrence.templateName,
                      avec: l.personnes, commenceeA: null, faite: false,
                    })} style={{ ...btn('#fff', C.brand), padding: '5px 10px', fontSize: '0.85rem' }}>
                      Faire moi-même
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ---------- la restitution au briefing ---------- */}
      {week && (
        <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontSize: '0.9rem', color: C.ink }}>
            Semaine {week.isoWeek} : la restitution de 5 minutes au briefing — « une routine sans public meurt en trois mois ».
          </div>
          {week.debriefDoneAt
            ? <span style={{ color: C.ok, fontWeight: 700 }}>Faite le {String(week.debriefDoneAt).slice(0, 10)}</span>
            : (
              <button type="button" style={btn('#fff', C.brand)} onClick={async () => {
                try { await safetyRoutines.debrief({}); setFlash('Restitution au briefing enregistrée.'); charger(); }
                catch (e) { setErr(errMsg(e)); }
              }}>Restitution faite au briefing</button>
            )}
        </div>
      )}
    </div>
  );
}
